import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Tab,
  Tabs,
} from "@mui/material";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import RequestPageMUI from "./RequestPageMUI";
import apiClient from "@/api/client";

const DanaBreakfastPaymentRequests = () => {
  // Tab state
  const [tab, setTab] = useState(0);

  // --- State for the approval dialog (requests table) ---
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [approveData, setApproveData] = useState({
    monks_count: "",
    laypeople_count: "",
    amount: "",
    reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // No need for API_BASE_URL or getAuthToken – apiClient handles them

  const handleApprove = (row, newStatus) => {
    setCurrentRow(row);
    setApproveData({ monks_count: "", laypeople_count: "", amount: "", reason: "" });
    setApproveDialogOpen(true);
  };

  const handleApproveSubmit = async () => {
    const { monks_count, laypeople_count, amount, reason } = approveData;
    if (!monks_count || !laypeople_count || !amount || !reason) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "All fields are required",
        confirmButtonColor: "#c49b36",
      });
      return;
    }
    setSubmitting(true);
    try {
      const response = await apiClient.put(
        `/approve-dana-payment-request/${currentRow.id}/approve`,
        {
          reason,
          monks_count: parseInt(monks_count, 10),
          laypeople_count: parseInt(laypeople_count, 10),
          amount: parseFloat(amount),
        }
      );
      const data = response.data;

      Swal.fire({
        icon: "success",
        title: "Success",
        text: data.message,
        confirmButtonColor: "#c49b36",
      });
      setApproveDialogOpen(false);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || error.message,
        confirmButtonColor: "#c49b36",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Tabs
        value={tab}
        onChange={(_, newValue) => setTab(newValue)}
        TabIndicatorProps={{ style: { backgroundColor: '#c49b36', height: 3 } }}
      >
        <Tab
          label="Breakfast Payment Requests"
          sx={{ fontWeight: 'bold', fontSize: '1rem' }}
        />
        <Tab
          label="Breakfast Payments"
          sx={{ fontWeight: 'bold', fontSize: '1rem' }}
        />
      </Tabs>

      {/* Tab 0: Breakfast Payment Requests (with approval) */}
      {tab === 0 && (
        <>
          <RequestPageMUI
            key={refreshKey}
            title="Breakfast - Dana Payments"
            fetchUrl="/get-breakfast-dana-payments"
            editEndpoint="edit-dana-payment-request"
            deleteEndpoint="delete-dana-payment-request"
            statusEndpoint="updateStatus-dana-payment-request"
            dateFields={["dana_event_date", "created_at", "updated_at", "approved_at", "rejected_at", "deleted_at"]}
            hiddenFields={["ip_address", "deleted_at", 'member_id', 'member']}
            onApprove={handleApprove}
          />

          {/* Approval Dialog */}
          <Dialog
            open={approveDialogOpen}
            onClose={() => !submitting && setApproveDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            sx={{ zIndex: 1000 }}
          >
            <DialogTitle>Approve Breakfast Dana Payment Request</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Reference: {currentRow?.reference_no}
                </Typography>
                <TextField
                  autoFocus
                  margin="dense"
                  label="Number of Monks"
                  type="number"
                  fullWidth
                  value={approveData.monks_count}
                  onChange={(e) => setApproveData({ ...approveData, monks_count: e.target.value })}
                  required
                  inputProps={{ min: 0 }}
                />
                <TextField
                  margin="dense"
                  label="Number of Laypeople"
                  type="number"
                  fullWidth
                  value={approveData.laypeople_count}
                  onChange={(e) => setApproveData({ ...approveData, laypeople_count: e.target.value })}
                  required
                  inputProps={{ min: 0 }}
                />
                <TextField
                  margin="dense"
                  label="Payment Amount (AUD)"
                  type="number"
                  fullWidth
                  value={approveData.amount}
                  onChange={(e) => setApproveData({ ...approveData, amount: e.target.value })}
                  required
                  inputProps={{ step: "0.01", min: 0.01 }}
                />
                <TextField
                  margin="dense"
                  label="Message to Donor (Reason / Note)"
                  multiline
                  rows={3}
                  fullWidth
                  value={approveData.reason}
                  onChange={(e) => setApproveData({ ...approveData, reason: e.target.value })}
                  required
                  placeholder="E.g., Dana details, special instructions..."
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setApproveDialogOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={handleApproveSubmit}
                variant="contained"
                color="success"
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={24} /> : "Confirm Approval"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Tab 1: Breakfast Payments (read‑only) */}
      {tab === 1 && (
        <RequestPageMUI
          title="Breakfast Payments"
          fetchUrl="/get-payments?payment_type=dana_request&meal_type=breakfast"
          disableActions={true}
          statusEndpoint={null}
          editEndpoint={null}
          deleteEndpoint={null}
          tableId="payments_breakfast"
          enableColumnReorder={true}
          persistToDatabase={true}
          serverSideSorting={true}
          hiddenFields={[
            'id', 'payment_type', 'related_id', 'access_token', 'request_token', 'authorisation_code',
            'response_code', 'response_message', 'gateway_name',
            'integration_type', 'payment_match_status', 'updated_by', 'created_by'
          ]}
          dateFields={['paid_at', 'created_at', 'updated_at']}
          statusField="status"
        />
      )}
    </Box>
  );
};

export default DanaBreakfastPaymentRequests;